import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ViewRef,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { SafeContentPipe } from '@warema/shared/pipes';
import { first } from 'rxjs';
import { SvgCacheService } from '../../services/svg-cache.service';

@Component({
    selector: 'warema-svg-icon',
    templateUrl: './svg-icon.component.html',
    styleUrls: ['./svg-icon.component.scss'],
    imports: [SafeContentPipe],
    host: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '[style.transform]': 'hostTransforms()',
    },
})
export class SvgIconComponent {
    private readonly _domSanitizer = inject(DomSanitizer);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _destroyRef = inject(DestroyRef);
    public readonly svgCacheService = inject(SvgCacheService);

    public readonly filePath = input.required<string>();
    public readonly width = input<number | string | null>(null);
    public readonly height = input<number | string | null>(null);

    public readonly rotate = input<number | undefined>(undefined);
    public readonly flipHorizontal = input(false);
    public readonly flipVertical = input(false);

    public readonly svgCacheEnabled = input(true);

    public readonly loadingFinished = output<void>();

    public readonly widthInternal = computed(() => {
        return this._parseSizeParam(this.width());
    });

    public readonly heightInternal = computed(() => {
        return this._parseSizeParam(this.height());
    });

    public readonly hostTransforms = computed(() => {
        const transforms = [];
        if (this.flipHorizontal()) {
            transforms.push(`scaleX(-1)`);
        }
        if (this.flipVertical()) {
            transforms.push(`scaleY(-1)`);
        }

        const rotate = this.rotate();
        if (rotate != null) {
            transforms.push(`rotate(${Number(rotate)}deg)`);
        }

        return transforms.length > 0 ? this._domSanitizer.bypassSecurityTrustStyle(transforms.join(' ')) : null;
    });

    public readonly svg = signal<string>('');

    private readonly _fallbackSvgPath = 'shared-assets/icons/image.svg';

    constructor() {
        this._changeDetectorRef.detach();

        effect(() => {
            const width = this.width();
            const height = this.height();
            const filePath = this.filePath();
            const includeSize = width != null || height != null;
            this.hostTransforms();

            this._triggerChange();

            this.svgCacheService
                .fetchSvgFromPath(filePath, this._fallbackSvgPath, includeSize)
                .pipe(first(), takeUntilDestroyed(this._destroyRef))
                .subscribe(svgContent => {
                    this.svg.set(svgContent);
                    this._triggerChange();
                    this.loadingFinished.emit();
                });
        });
    }

    private _parseSizeParam(value: number | string): string {
        if (!value) {
            return '100%';
        }

        const numValue = Number(value);
        if (Number.isNaN(numValue)) {
            return '100%';
        } else if (value.toString().includes('%')) {
            return `${numValue}%`;
        } else {
            return `${numValue}px`;
        }
    }

    private _triggerChange(): void {
        if (!(this._changeDetectorRef as ViewRef).destroyed) {
            this._changeDetectorRef.detectChanges();
        }
    }
}
